<script setup lang="ts">
import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

interface TestimonialItemProps {
  data: TestimonialRecord
  layout?: 'column' | 'row'
  isScrubbed?: boolean
  isVisible?: boolean
}

const props = withDefaults(defineProps<TestimonialItemProps>(), {
  layout: 'row',
  isScrubbed: false,
  isVisible: false
})

const { isDesktop } = useDevice()
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  if (ctxRef.value && props.isScrubbed) {
    gsap.registerPlugin(ScrollTrigger, SplitText)

    ctx = gsap.context(() => {
      const split = new SplitText('.fx-quote', { type: 'lines' })
      split.lines.forEach((target) => {
        gsap.to(target, {
          backgroundPositionX: 0,
          ease: 'none',
          scrollTrigger: props.isScrubbed
            ? {
                trigger: target,
                markers: false,
                scrub: 1,
                start: () => (isDesktop ? 'top bottom' : 'top 75%'),
                end: () => (isDesktop ? 'bottom 50%' : 'bottom 75%')
              }
            : undefined
        })
      })
      ScrollTrigger.refresh()
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    :class="{
      'lg:grid-default': layout === 'row'
    }"
  >
    <div
      v-if="data.quote"
      class="fx-quote text-m mb-8 italic lg:text-4xl"
      :class="{
        'items-start text-left lg:order-2 lg:col-span-8': layout === 'row',
        'text-center': layout === 'column'
      }"
    >
      {{ data.quote }}
    </div>
    <div
      :class="{
        'lg:order-1 lg:col-span-4': layout === 'row'
      }"
    >
      <figure class="flex w-fit gap-4 lg:gap-11" :class="layout === 'row' ? 'ml-0' : 'mx-auto'">
        <div class="flex-shrink-0 lg:w-18">
          <Image
            v-if="data.media"
            loading="lazy"
            effect="mask"
            class="aspect-square h-16 w-16 overflow-hidden rounded-full object-cover lg:h-18 lg:w-18"
            :src="data.media?.url"
            :srcset="
              data.media?.responsiveImage ? data.media?.responsiveImage.webpSrcSet : undefined
            "
            :width="data.media?.width ?? 80"
            :height="data.media?.height ?? 80"
            :alt="data.media?.alt ?? ''"
          />
        </div>
        <figcaption>
          <footer>
            <div v-if="data.name" class="mb-1 text-xl font-bold lg:text-2xl">
              <cite class="not-italic">{{ data.name }}</cite>
            </div>
            <div>
              <div v-if="data.title" class="leading-tight lg:text-lg lg:leading-tight">
                {{ data.title }}
              </div>
              <div v-if="data.company" class="mt-1 leading-tight lg:text-lg lg:leading-tight">
                {{ data.company }}
              </div>
            </div>
          </footer>
        </figcaption>
      </figure>
    </div>
  </div>
</template>

<style lang="postcss">
.fx-quote > div {
  background: linear-gradient(to right, rgb(255, 255, 255) 50%, rgb(37, 37, 37) 50%);
  background-size: 200% 100%;
  background-position-x: 100%;
  color: transparent;
  background-clip: text;
  -webkit-background-clip: text;
}
</style>
